/**
 * Действия настроек воронки вне createSlice фасада (сведение стильпаков,
 * WEB-3718): свои шрифты (слово владельца 20.10.2026, ветка fonts) и своё
 * боковое поле экрана (задание stylepack/screen-padding). Ветки завели их
 * редьюсерами внутри createSlice, и фасад editorSlice.ts перерос храповик
 * длины (FACADE_MAX_LINES в store/__tests__/editorSlice.test.ts). Приём — как
 * у `./screenFieldActions.ts`: отдельный createAction, тело здесь, фасад
 * реэкспортирует создатели под прежними именами и ловит их в extraReducers
 * через `addFunnelSettingsCases`. Строковые имена действий прежние байт в
 * байт; одно действие — один шаг «Отменить» (withHistoryPayload). Тела
 * перенесены из фасада без изменений.
 *
 * WEB-3399: `setCustomStyle` — свой стиль организации у открытой части (окно
 * «Оформление» у экрана): ссылка `custom:<uuid>` и копия, а при выборе из
 * «Моих стилей» — и поля тела, одним шагом «Отменить». Создатель берут
 * напрямую отсюда, фасад его не реэкспортирует (фасад не растёт).
 * `setOwnPalette` — своя палитра у открытой части (правка «Своей палитры» и
 * выбор из «Моих палитр»): правило `withOwnPalette`, одним шагом «Отменить»;
 * так же только отсюда.
 *
 * WEB-4144: `applyTemplateLook` — «Применить шаблон» → «Только внешний вид» у
 * открытой части (окно «Оформление» у экрана): пак, тема, шрифт и
 * `templateId` шаблона, слой «Оригинал» снят (`withTemplateLook`), одним шагом
 * «Отменить». Создатель — так же напрямую отсюда.
 */
import { createAction } from '@reduxjs/toolkit';
import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import { withHistoryPayload } from './history';
import { readScreenPaddingX } from '@/pages/funnel/funnelSurfaceStyle';
import { withPackColors } from './packComposition';
import { customFontFamilyOf, type CustomFontFile } from '@/themes/customFonts';
import {
  withCustomFontFileIn,
  withCustomStyle,
  withFunnelFontId,
  withOwnPalette,
  withOwnStyleBody,
  withoutCustomFontIn,
  type OwnPaletteColors,
} from './appearanceEdits';
import { hasOriginalLookBlocks, withLookChoice } from './originalLook';
import { withTemplateLook, type TemplateLookSource } from './templateLook';
import type {
  CustomStylePackCopy,
  OrgStyleBody,
} from '@/themes/customStylePack';

/**
 * Свой шрифт воронки: загруженный файл — в семейство (новое заводится;
 * пределы и занятые имена — `withCustomFontFile`, при отказе состояние то же).
 */
export const addCustomFontFile = createAction<
  { family: string; file: CustomFontFile },
  'editor/addCustomFontFile'
>('editor/addCustomFontFile');

/**
 * Убрать своё семейство; ссылка `custom:<семейство>` снимается и у воронки, и
 * у блоков (свежий взгляд WEB-3718) — иначе блок ссылался бы на шрифт,
 * которого нет. Блок без ссылки берёт шрифт воронки, как «Как у воронки».
 */
export const removeCustomFont = createAction<string, 'editor/removeCustomFont'>(
  'editor/removeCustomFont',
);

/** Шрифт всей воронки: встроенный id или `custom:<семейство>`; undefined снимает. */
export const setFunnelFontId = createAction<
  string | undefined,
  'editor/setFunnelFontId'
>('editor/setFunnelFontId');

/**
 * Своё боковое поле экрана. Число судит та же дверь, что читают плеер и
 * предпросмотр; пусто или мусор снимают поле — «как у стиля» в настройках не
 * хранится.
 */
export const setScreenPaddingX = createAction<
  number | undefined,
  'editor/setScreenPaddingX'
>('editor/setScreenPaddingX');

/**
 * «Взять цвета пака» (WEB-3718, решение владельца 23.10 ~04:20): тема пака,
 * свои палитра, фон и цвет текста сняты, признак «трогал цвета» снят.
 */
export const takePackColors = createAction('editor/takePackColors');

/**
 * Свой стиль: копия (и тело своего стиля из «Моих стилей») в настройки
 * открытой части; часть — `editorType` (у пейволла поля квиза не пишутся).
 */
export const setCustomStyle = createAction<
  { copy: CustomStylePackCopy; body?: OrgStyleBody },
  'editor/setCustomStyle'
>('editor/setCustomStyle');

function setCustomStyleReducer(
  state: EditorState,
  action: PayloadAction<{ copy: CustomStylePackCopy; body?: OrgStyleBody }>,
): EditorState {
  const { copy, body } = action.payload;
  const part = state.editorType === 'paywall' ? 'paywall' : 'quiz';
  // WEB-4151: у части со слоем «оригинала» — сперва то же правило вида, что у
  // готового пака (поля слоя с блоков и флаг «Оригинал» снимаются), одним шагом.
  const look = hasOriginalLookBlocks(state.settings, state.content)
    ? withLookChoice(state.settings, state.content, copy.basePackId)
    : { settings: state.settings, screens: state.content };
  const settings = body
    ? withOwnStyleBody(look.settings, copy, body, part)
    : withCustomStyle(look.settings, copy);
  return { ...state, settings, content: look.screens, isDirty: true };
}

/** Своя палитра у открытой части: палитра, без своих фона и цвета текста. */
export const setOwnPalette = createAction<
  OwnPaletteColors,
  'editor/setOwnPalette'
>('editor/setOwnPalette');

function setOwnPaletteReducer(
  state: EditorState,
  action: PayloadAction<OwnPaletteColors>,
): EditorState {
  return {
    ...state,
    settings: withOwnPalette(state.settings, action.payload),
    isDirty: true,
  };
}

/** Внешний вид шаблона в открытую часть (WEB-4144). */
export const applyTemplateLook = createAction<
  { tpl: TemplateLookSource; keepColors: boolean },
  'editor/applyTemplateLook'
>('editor/applyTemplateLook');

function applyTemplateLookReducer(
  state: EditorState,
  action: PayloadAction<{ tpl: TemplateLookSource; keepColors: boolean }>,
): EditorState {
  const { tpl, keepColors } = action.payload;
  const r = withTemplateLook(state.settings, state.content, tpl, { keepColors });
  return { ...state, settings: r.settings, content: r.screens, isDirty: true };
}

function takePackColorsReducer(state: EditorState): EditorState {
  const settings = withPackColors(state.settings);
  return settings === state.settings
    ? state
    : { ...state, settings, isDirty: true };
}

function addCustomFontFileReducer(
  state: EditorState,
  action: PayloadAction<{ family: string; file: CustomFontFile }>,
): EditorState {
  // WEB-3767: правило правки настроек — `withCustomFontFileIn`
  // (`./appearanceEdits.ts`); список не изменился — состояние то же.
  const settings = withCustomFontFileIn(
    state.settings,
    action.payload.family,
    action.payload.file,
  );
  if (settings === state.settings) return state;
  return { ...state, settings, isDirty: true };
}

function removeCustomFontReducer(
  state: EditorState,
  action: PayloadAction<string>,
): EditorState {
  // Настройки — `withoutCustomFontIn` (`./appearanceEdits.ts`, WEB-3767);
  // ссылки блоков на это семейство чистятся здесь: это про содержимое.
  const family = action.payload.toLowerCase();
  const refersToRemoved = (id: string | undefined): boolean =>
    customFontFamilyOf(id)?.toLowerCase() === family;
  return {
    ...state,
    content: state.content.map((screen) =>
      screen.blocks.some((block) => refersToRemoved(block.fontId))
        ? {
            ...screen,
            blocks: screen.blocks.map((block) => {
              if (!refersToRemoved(block.fontId)) return block;
              const { fontId: _drop, ...restBlock } = block;
              return restBlock as typeof block;
            }),
          }
        : screen,
    ),
    settings: withoutCustomFontIn(state.settings, action.payload),
    isDirty: true,
  };
}

function setFunnelFontIdReducer(
  state: EditorState,
  action: PayloadAction<string | undefined>,
): EditorState {
  return {
    ...state,
    settings: withFunnelFontId(state.settings, action.payload),
    isDirty: true,
  };
}

function setScreenPaddingXReducer(
  state: EditorState,
  action: PayloadAction<number | undefined>,
): EditorState {
  const px = readScreenPaddingX({ screenPaddingX: action.payload });
  const { screenPaddingX: _prev, ...rest } = state.settings;
  return {
    ...state,
    settings:
      px === undefined ? rest : { ...state.settings, screenPaddingX: px },
    isDirty: true,
  };
}

/** Регистрирует действия в extraReducers фасада; возвращает тот же builder для цепочки. */
export function addFunnelSettingsCases(
  builder: ActionReducerMapBuilder<EditorState>,
): ActionReducerMapBuilder<EditorState> {
  return builder
    .addCase(addCustomFontFile, withHistoryPayload(addCustomFontFileReducer))
    .addCase(removeCustomFont, withHistoryPayload(removeCustomFontReducer))
    .addCase(setFunnelFontId, withHistoryPayload(setFunnelFontIdReducer))
    .addCase(setScreenPaddingX, withHistoryPayload(setScreenPaddingXReducer))
    .addCase(takePackColors, withHistoryPayload(takePackColorsReducer))
    .addCase(setCustomStyle, withHistoryPayload(setCustomStyleReducer))
    .addCase(setOwnPalette, withHistoryPayload(setOwnPaletteReducer))
    .addCase(applyTemplateLook, withHistoryPayload(applyTemplateLookReducer));
}
